import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import DashboardLayout, { REPORT_TYPES } from './components/DashboardLayout';
import DashboardHome from './components/DashboardHome';
import V1V9Upload from './components/16sv1-v9/16sv1-v9-report-upload';
import  MRNAReportUpload from './components/mRNA/mRNA-report-upload';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import AdminPanel from './components/AdminPanel';
import { AuthProvider, useAuth } from './context/AuthContext';
import UploadSection from './components/UploadSection';
import ShotgunReportUpload from './components/Shotgun/ShotgunReportUpload';
import BacterialWgsReportUpload from './components/BacterialWGS/BacterialWgsReportUpload';
import WesReportUpload from './components/WES/WesReportUpload';

function ComingSoon({ label, dark }) {
  return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      minHeight:'60vh', gap:14,
      fontFamily:"'IBM Plex Mono',monospace",
      color: dark ? '#3a5a78' : '#9ab0c8',
    }}>
      <svg width={44} height={44} viewBox="0 0 16 16" fill="none"
        stroke="currentColor" strokeWidth="1.1" strokeLinecap="round">
        <circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 2"/>
      </svg>
      <div style={{ fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase' }}>
        {label} — Coming Soon
      </div>
    </div>
  );
}

// ── App shell (inside AuthProvider) ──────────────────────────────────────────
function AppShell() {
  const { user, logout, isAuthenticated, loading } = useAuth();

  const [screen, setScreen]       = useState('login');
  const [showAdmin, setShowAdmin] = useState(false);
  const [dark, setDark]           = useState(false);
  const [activeType, setActiveType] = useState('dashboard');
  const [activeTab, setActiveTab]   = useState('upload');

  // ── Reset admin panel whenever a DIFFERENT user logs in ──────────────────
  const prevUserId = useRef(null);
  useEffect(() => {
    const currentId = user?._id || user?.username || null;
    if (currentId !== prevUserId.current) {
      setShowAdmin(false);          // never carry over admin state to a new session
      prevUserId.current = currentId;
    }
  }, [user]);

  const [reportUrlsMap, setReportUrlsMap] = useState(
    Object.fromEntries(REPORT_TYPES.map(rt => [rt.id, { html:'', qcExcel:'', mappingExcel:'', variantExcel:'' }]))
  );

  const setReportUrls = (typeId) => (urls) =>
    setReportUrlsMap(prev => ({ ...prev, [typeId]: urls }));

  const reportReady = Object.fromEntries(
    REPORT_TYPES.map(rt => [rt.id, Boolean(reportUrlsMap[rt.id]?.html)])
  );

  const handleNavigate = (typeId, tab) => {
    setActiveType(typeId); setActiveTab(tab);
  };

  // Loading spinner
  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0a1628', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:32, height:32, borderWidth: 2, borderStyle: 'solid', borderColor: 'rgba(0,180,216,0.15)', borderTopColor: '#00b4d8', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // Not authenticated — show login or register
  if (!isAuthenticated) {
    if (screen === 'register')
      return <RegisterPage onGoLogin={() => setScreen('login')} />;
    return <LoginPage onGoRegister={() => setScreen('register')} />;
  }

  // Admin panel — full screen overlay
  if (showAdmin) {
    return <AdminPanel onBack={() => setShowAdmin(false)} />;
  }

  // Dashboard content
  const renderContent = () => {
    if (activeType === 'dashboard') return (
      <DashboardHome dark={dark} t={buildThemeTokens(dark)} reportReadyMap={reportReady} onNavigate={handleNavigate} />
    );
    if (activeType === 'wgs')           return <UploadSection key="wgs" setReportUrls={setReportUrls('wgs')} setActiveTab={setActiveTab} dark={dark} />;
    if (activeType === 'bacterial_wgs') return <BacterialWgsReportUpload key="bacterial_wgs" setReportUrls={setReportUrls('bacterial_wgs')} setActiveTab={setActiveTab} dark={dark} />;
    if (activeType === '16sv1-v9')      return <V1V9Upload key="16sv1-v9" setReportUrls={setReportUrls('16sv1-v9')} setActiveTab={setActiveTab} dark={dark} />;
    if (activeType === 'mRNA')          return <MRNAReportUpload key="mRNA" setReportUrls={setReportUrls('mRNA')} setActiveTab={setActiveTab} dark={dark} />;
    if (activeType === 'meta')          return <ShotgunReportUpload key="meta" setReportUrls={setReportUrls('meta')} setActiveTab={setActiveTab} dark={dark} />;
    if (activeType === 'wes')           return <WesReportUpload key="wes" setReportUrls={setReportUrls('wes')} setActiveTab={setActiveTab} dark={dark} />;
    return <ComingSoon label={REPORT_TYPES.find(r => r.id === activeType)?.label} dark={dark} />;
  };

  return (
    <DashboardLayout
      activeType={activeType}
      setActiveType={(id) => { setActiveType(id); if (id !== 'dashboard') setActiveTab('upload'); }}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      reportReady={reportReady}
      dark={dark}
      setDark={setDark}
      user={user}
      onLogout={logout}
      onOpenAdmin={user?.role === 'admin' ? () => setShowAdmin(true) : undefined}
    >
      {renderContent()}
    </DashboardLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

function buildThemeTokens(dark) {
  return dark ? {
    cardBg:'#0f2040', cardBorder:'rgba(255,255,255,0.08)',
    text:'#e8edf4', textSub:'#8aaac8', muted:'#3d5a78', dark:true,
  } : {
    cardBg:'#ffffff', cardBorder:'rgba(0,0,0,0.09)',
    text:'#1a2a3a', textSub:'#5a7a9a', muted:'#a0b4c8', dark:false,
  };
}